import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';

import { LocationSearchRequestLogging } from '../models/amplitude-properties.model';

import {
  hasOfferSaverPlus,
  HotelPricingSummary,
  HotelSearchResponse,
  MealOption,
  RoomPriceOption,
  totalOffersWithSaverPlus,
} from '@rx-applications/clientsite/shared/utils';

@Injectable({ providedIn: 'root' })
export class LoggingService {
  didSearchParamMapping(searchBoxValues: Params) {
    return {
      'Lead Time': differenceInCalendarDays(
        new Date(searchBoxValues['checkoutDate']),
        new Date(searchBoxValues['checkinDate']),
      ),
      'Location Code': searchBoxValues['locationCode'],
      'Location Name': searchBoxValues['locationName'],
      'Hotel Id': searchBoxValues['hotelId'] || '',
      'Hotel Name': searchBoxValues['hotelName'] || '',
      'Check in': searchBoxValues['checkinDate'],
      'Check out': searchBoxValues['checkoutDate'],
      // 'Force Filter': state.location.count > this._initializer.liteConfig.maxHotelsInSearch ? 'true' : 'false',
      Rooms: searchBoxValues['rooms']
        .split(',')
        .map((r: string, i: number) => (+r > 0 ? `Room ${i}: ${r}` : ''))
        .filter((item: string) => item.length)
        .join(', '),
      Stars: searchBoxValues['stars'],
      'Payment Option': searchBoxValues['payment'],
      // 'Currency': state.currency.code,
      'Meal Option': this.getmealOption(+searchBoxValues['meal']),
      'Page Logic': searchBoxValues['pageLogic'],
    };
  }

  landedOnRoomSelectParamMapping(
    results: HotelSearchResponse,
    nights: number,
    startApiTime: number,
    selectedHotel: HotelPricingSummary,
  ) {
    let optionCount = 0;
    const allPrices = (<number[]>[]).concat(
      ...results.roomOptions.map((room) =>
        room.priceOptions.map((option) => Math.round((option.sellPrice / nights) * 100) / 100),
      ),
    );
    const roomOptionsLoggingCount = this.#getPriceOptionsCount(results);

    results.roomOptions.forEach((room) => (optionCount += room.priceOptions.length));
    const sumPrices = allPrices.reduce((previous: number, current: number) => current + previous, 0);
    const lowMiddle = Math.floor((allPrices.length - 1) / 2);
    const highMiddle = Math.ceil((allPrices.length - 1) / 2);
    const allOptions = results.roomOptions.reduce((accum, ele) => {
      return [...accum, ...ele.priceOptions];
    }, []);
    const nonRefundables = allOptions.filter((roomOption) => /Non[-\s]Refundable/.test(roomOption.roomName));
    const hasRefundable = allOptions.length !== nonRefundables.length;
    const allRefundable = nonRefundables.length === 0;

    let refundableStatus = 'false';
    if (hasRefundable && !allRefundable) {
      refundableStatus = 'true';
    } else if (hasRefundable && allRefundable) {
      refundableStatus = 'unknown';
    }

    const hasMeal =
      results.roomOptions
        .map((room) => room.priceOptions.findIndex((option) => option.mealPlanCode === 0))
        .findIndex((result) => result === -1) > -1;
    const hasDouble =
      results.roomOptions
        .map((room) =>
          room.priceOptions.findIndex((option) => option.roomName.includes('Twin') || option.roomName.includes('Double')),
        )
        .findIndex((result) => result > -1) > -1;

    const hasSaverPlus =
      results.roomOptions && results.roomOptions.length
        ? totalOffersWithSaverPlus(results.roomOptions[0].priceOptions)
        : false;

    return {
      SearchPriceDiscrepancy: this.hasSearchPriceDiscrepancy(selectedHotel, results),
      HasAvailability: results.roomOptions.length > 0,
      ApiResponseTime: Math.round((performance.now() - startApiTime) / 1000),
      HotelName: results.hotelName,
      HotelId: results.hotelId,
      RoomOptions: {
        Count: optionCount,
        ...roomOptionsLoggingCount,
        MaxPrice: optionCount > 0 ? Math.max(...allPrices) : 0,
        MinPrice: optionCount > 0 ? Math.min(...allPrices) : 0,
        MedianPrice: optionCount > 0 ? (allPrices[lowMiddle] + allPrices[highMiddle]) / 2 : 0,
        AveragePrice: optionCount > 0 ? Math.round((sumPrices / allPrices.length) * 100) / 100 : 0,
        HasMealOption: hasMeal,
        HasDoubleRoomOption: hasDouble,
        HasRefundableOption: refundableStatus,
        SaverPlus: hasSaverPlus,
      },
    };
  }

  locationSearchResponseParamMapping(
    searchQuery: LocationSearchRequestLogging,
    recommended: HotelPricingSummary[],
    other: HotelPricingSummary[],
    nearby: HotelPricingSummary[],
  ) {
    const nights = differenceInCalendarDays(new Date(searchQuery.departureDate), new Date(searchQuery.arrivalDate));
    const prices = [...recommended, ...other].map((res) => res.price / nights);

    return {
      NoRecommend: recommended.length,
      NoOther: other.length,
      NoNearby: nearby.length,
      NoTotal: recommended.length + other.length + nearby.length,
      FromCache: false,
      MaxPrice: prices.length ? Math.floor(Math.max(...prices)) : 0,
      MinPrice: prices.length ? Math.floor(Math.min(...prices)) : 0,
      Median: Math.floor(prices.sort((a, b) => b - a)[Math.floor(prices.length / 2)]) || 0,
      Average: Math.floor(prices.reduce((sum, cur) => sum + cur, 0) / prices.length) || 0,
      CountryCode: prices.length ? [...recommended, ...other].map((res) => res.countryCode).pop() : '',
      SaverPlus: totalOffersWithSaverPlus(recommended.concat(other)),
    };
  }

  roomOptionClickParamMapping(
    roomData: RoomPriceOption,
    inTerms: boolean,
    startTimestamp: number,
    rankSelected: number,
    totalRoomOptions: number,
  ) {
    const roomTitle = roomData.roomName.split(' - ');
    const nonRefundable = roomTitle.some((el) => /Non[-\s]Refundable/.test(el));
    const mealOption = roomTitle.filter((el) => !/Non[-\s]Refundable/.test(el)).splice(-1)[0] || '';

    return {
      TimeTakenForApiResponse: Math.round((performance.now() - startTimestamp) / 1000),
      Price: roomData.sellPrice,
      CurrencyCode: roomData.currency.code,
      RoomName: roomData.roomName,
      MealOption: mealOption,
      RoomNameIsNonRefundable: nonRefundable,
      IsWithinTerms: inTerms,
      IsOutOfPolicy: !roomData.inPolicy,
      SaverPlus: hasOfferSaverPlus(roomData),
      RankSelected: rankSelected,
      TotalRoomOptions: totalRoomOptions,
      LoyaltyGuaranteed: roomData.loyaltyGuaranteed,
    };
  }

  private getmealOption(code: number) {
    switch (code) {
      case MealOption.AllBreakfastOptions: {
        return 'B&B Only';
      }
      case MealOption.IncludesDinner: {
        return 'All Dinner Options';
      }
      case MealOption.AnyMealOption:
      default: {
        return 'Show all';
      }
    }
  }

  private hasSearchPriceDiscrepancy(selectedHotel: HotelPricingSummary, results: HotelSearchResponse): string {
    const hotelPriceSelectedFromSearchResults: number = selectedHotel?.price;

    return !hotelPriceSelectedFromSearchResults || !results.roomOptions?.length
      ? 'Not Applicable'
      : this.hasPriceDiscrepancy(results, hotelPriceSelectedFromSearchResults);
  }

  /**
   * Check if the price selected from the search results is within the price options of the selected hotel with a margin of 2
   * @param results
   * @param hotelPriceSelectedFromSearchResults
   * @private
   */
  private hasPriceDiscrepancy(results: HotelSearchResponse, hotelPriceSelectedFromSearchResults: number): string {
    const selectedPriceNotPresentInResults = !results.roomOptions
      ?.reduce((acc, current) => [...acc, current.priceOptions], [])
      .flat()
      .some(({ sellPrice }) => Math.abs(sellPrice - hotelPriceSelectedFromSearchResults) <= 2);

    return selectedPriceNotPresentInResults?.toString();
  }

  #getPriceOptionsCount(hotelResponse: HotelSearchResponse): { [key: string]: number } {
    const counts: { [key: string]: number } = {};

    hotelResponse.roomOptions.forEach((roomOption, index) => {
      const roomOptionsKey = `RoomOptionsRoom${index + 1}`;
      counts[roomOptionsKey] = roomOption.priceOptions.length;

      const mealPlanOptionsKey = `MealPlanOptionsRoom${index + 1}`;
      counts[mealPlanOptionsKey] = roomOption.priceOptions.filter((x) => x.mealPlanCode === 0).length;

      const refundableOptionsKey = `RefundableOptionsRoom${index + 1}`;
      counts[refundableOptionsKey] = roomOption.priceOptions.filter((x) => x.refundable).length;
    });

    return counts;
  }
}
